import { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { LoadingButton } from '@/components/LoadingButton';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import type { SourceTableDto } from '@/api/control/missionImpactAPI.schemas';
import { usePutSourceTableParent, readParentSourceTableId } from '@/api/source-table-parent';
import { buildParentTableOptions } from '@/lib/parentTableOptions';
import { useLanguage } from '@/contexts/LanguageContext';
import { useError } from '@/contexts/ErrorContext';

/** Sentinel <Select> value representing "no parent" (a root table); Select cannot hold empty string. */
const NONE_VALUE = '__none__';

interface SetParentDialogProps {
  orgId: string;
  profileId: string;
  sourceId: string;
  /** The table whose gating parent is being edited. */
  table: SourceTableDto;
  /** Candidate parents — the other tables in this source (self is filtered out here). */
  candidates: SourceTableDto[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after a successful save so the caller can refresh the list. */
  onSaved: () => void;
}

/**
 * Dialog for setting or clearing a source table's gating parent (issue #442). The parent picker
 * lists the other tables in the same source; choosing "None" clears the parent and returns the
 * table to running as a root. Cycle / cross-source / self validation is enforced server-side.
 */
export function SetParentDialog({
  orgId,
  profileId,
  sourceId,
  table,
  candidates,
  open,
  onOpenChange,
  onSaved,
}: SetParentDialogProps) {
  const { t } = useLanguage();
  const { handleApiError } = useError();

  const currentParentId = readParentSourceTableId(table);
  const [selected, setSelected] = useState<string>(currentParentId ?? NONE_VALUE);

  // Re-sync the selection whenever the dialog is (re)opened for a table, so it reflects the
  // table's current parent rather than a stale choice from a previous open.
  useEffect(() => {
    if (open) {
      setSelected(currentParentId ?? NONE_VALUE);
    }
  }, [open, currentParentId]);

  const { mutate, isPending } = usePutSourceTableParent({
    mutation: {
      onSuccess: () => {
        toast.success(t.profiles.sourceTables.parentUpdateSuccess);
        onOpenChange(false);
        onSaved();
      },
      onError: (error) => {
        handleApiError(error, { context: 'SetParentDialog.setParent' });
      },
    },
  });

  // Other tables in the source, sorted by display name and labelled "Display name (logical_name)"
  // to match the initial table picker. A table is never offered as its own parent.
  const parentOptions = useMemo(() => buildParentTableOptions(table, candidates), [table, candidates]);

  const handleSave = () => {
    mutate({
      orgId,
      profileId,
      sourceId,
      sourceTableId: table.id,
      data: { parentSourceTableId: selected === NONE_VALUE ? null : selected },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t.profiles.sourceTables.parentDialogTitle}</DialogTitle>
          <DialogDescription>{t.profiles.sourceTables.parentDialogDescription}</DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="parent-select">{t.profiles.sourceTables.parentLabel}</Label>
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger id="parent-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE_VALUE}>{t.profiles.sourceTables.parentNone}</SelectItem>
              {parentOptions.map((option) => (
                <SelectItem key={option.id} value={option.id}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            {t.wizard.common.cancel}
          </Button>
          <LoadingButton onClick={handleSave} isLoading={isPending}>
            {t.wizard.common.save}
          </LoadingButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
